<template>
  <!-- Main container -->
  <div class="w-full space-y-6 p-2">

    <!-- Título e Botão Nova Conta -->
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-3xl font-bold text-gray-900">Contas</h1>
      <button @click="openNewAccountModal" class="bg-primary-dark hover:bg-primary text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-200">
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Nova Conta
      </button>
    </div>

    <!-- Cards Grid Superior -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full mb-6">

      <!-- Card Total a Receber -->
      <div class="bg-gradient-to-br from-white to-emerald-50 rounded-xl p-4 shadow-lg border border-emerald-100 w-full">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-3">
                <div class="bg-emerald-100 p-2 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 0118 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-800">Total a Receber</h3>
                  <p class="text-sm text-gray-500">{{ new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }) }}</p>
                </div>
              </div>
              <div class="text-2xl font-bold text-emerald-600">{{ formatCurrency(monthTotals.receivable.total) }}</div>
            </div>
            <div class="grid grid-cols-4 gap-2">
              <div class="p-2 bg-white rounded-lg">
                <div class="flex items-center justify-between">
                  <p class="text-xs text-gray-500">Recebido</p>
                  <p class="text-xs text-gray-400">({{ monthTotals.receivable.receivedCount }})</p>
                </div>
                <p class="text-sm font-semibold text-emerald-600">{{ formatCurrency(monthTotals.receivable.received) }}</p>
              </div>
              <div class="p-2 bg-white rounded-lg">
                <div class="flex items-center justify-between">
                  <p class="text-xs text-gray-500">Pendente</p>
                  <p class="text-xs text-gray-400">({{ monthTotals.receivable.pendingCount }})</p>
                </div>
                <p class="text-sm font-semibold text-amber-600">{{ formatCurrency(monthTotals.receivable.pending) }}</p>
              </div>
              <div class="p-2 bg-white rounded-lg">
                <div class="flex items-center justify-between">
                  <p class="text-xs text-gray-500">Agendado</p>
                  <p class="text-xs text-gray-400">({{ monthTotals.receivable.scheduledCount }})</p>
                </div>
                <p class="text-sm font-semibold text-blue-600">{{ formatCurrency(monthTotals.receivable.scheduled) }}</p>
              </div>
              <div class="p-2 bg-white rounded-lg">
                <div class="flex items-center justify-between">
                  <p class="text-xs text-gray-500">Atrasado</p>
                  <p class="text-xs text-gray-400">({{ monthTotals.receivable.overdueCount }})</p>
                </div>
                <p class="text-sm font-semibold text-red-600">{{ formatCurrency(monthTotals.receivable.overdue) }}</p>
              </div>
            </div>
          </div>

          <!-- Card Total a Pagar -->
          <div class="bg-gradient-to-br from-white to-rose-50 rounded-xl p-4 shadow-lg border border-rose-100 w-full">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-3">
                <div class="bg-rose-100 p-2 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H7a3 3 0 00-3 3v12a3 3 0 003 3z" />
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-800">Total a Pagar</h3>
                  <p class="text-sm text-gray-500">{{ new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }) }}</p>
                </div>
              </div>
              <div class="text-2xl font-bold text-rose-600">{{ formatCurrency(monthTotals.payable.total) }}</div>
            </div>
            <div class="grid grid-cols-4 gap-2">
              <div class="p-2 bg-white rounded-lg">
                <div class="flex items-center justify-between">
                  <p class="text-xs text-gray-500">Pago</p>
                  <p class="text-xs text-gray-400">({{ monthTotals.payable.paidCount }})</p>
                </div>
                <p class="text-sm font-semibold text-rose-600">{{ formatCurrency(monthTotals.payable.paid) }}</p>
              </div>
              <div class="p-2 bg-white rounded-lg">
                <div class="flex items-center justify-between">
                  <p class="text-xs text-gray-500">Pendente</p>
                  <p class="text-xs text-gray-400">({{ monthTotals.payable.pendingCount }})</p>
                </div>
                <p class="text-sm font-semibold text-amber-600">{{ formatCurrency(monthTotals.payable.pending) }}</p>
              </div>
              <div class="p-2 bg-white rounded-lg">
                <div class="flex items-center justify-between">
                  <p class="text-xs text-gray-500">Agendado</p>
                  <p class="text-xs text-gray-400">({{ monthTotals.payable.scheduledCount }})</p>
                </div>
                <p class="text-sm font-semibold text-blue-600">{{ formatCurrency(monthTotals.payable.scheduled) }}</p>
              </div>
              <div class="p-2 bg-white rounded-lg">
                <div class="flex items-center justify-between">
                  <p class="text-xs text-gray-500">Atrasado</p>
                  <p class="text-xs text-gray-400">({{ monthTotals.payable.overdueCount }})</p>
                </div>
                <p class="text-sm font-semibold text-red-600">{{ formatCurrency(monthTotals.payable.overdue) }}</p>
              </div>
            </div>
          </div>
        </div>
    

<!-- Card Saldo Projetado (Largura Total) -->
<div class="bg-gradient-to-br from-white to-indigo-50 rounded-xl p-4 shadow-lg border border-indigo-100 mb-6">
  <div class="flex items-center justify-between mb-4">
    <div class="flex items-center gap-3">
      <div class="bg-indigo-100 p-2 rounded-lg">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      </div>
      <div>
        <div class="flex items-center gap-6">
          <h3 class="text-lg font-semibold text-gray-800 whitespace-nowrap">Saldo Projetado</h3>
          <div class="flex items-center gap-4">
            <div class="flex items-center gap-2">
              <label class="text-sm text-gray-600">De:</label>
              <Calendar 
                v-model="balanceStartDate" 
                :locale="ptBR" 
                dateFormat="dd/mm/yy" 
                class="w-32" 
                :minDate="null"
                :maxDate="null"
              />
            </div>
            <div class="flex items-center gap-2">
              <label class="text-sm text-gray-600">Até:</label>
              <Calendar 
                v-model="balanceEndDate" 
                :locale="ptBR" 
                dateFormat="dd/mm/yy" 
                class="w-32" 
              />
            </div>
          </div>
        </div>
        <p class="text-sm text-gray-500">{{ new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }) }}</p>
      </div>
    </div>
    <div class="text-2xl font-bold text-indigo-600">{{ formatCurrency(filteredProjectedBalance) }}</div>
  </div>

  <!-- Grid de Valores -->
  <div class="grid grid-cols-4 gap-2">
    <div class="p-2 bg-white rounded-lg">
      <div class="flex items-center justify-between">
        <p class="text-xs text-gray-500">A Receber</p>
        <p class="text-xs text-gray-400">({{ receivableCount }})</p>
      </div>
      <p class="text-sm font-semibold text-emerald-600">{{ formatCurrency(receivableTotal) }}</p>
    </div>
    <div class="p-2 bg-white rounded-lg">
      <div class="flex items-center justify-between">
        <p class="text-xs text-gray-500">Recebido</p>
        <p class="text-xs text-gray-400">({{ receivedCount }})</p>
      </div>
      <p class="text-sm font-semibold text-amber-600">{{ formatCurrency(receivedTotal) }}</p>
    </div>
    <div class="p-2 bg-white rounded-lg">
      <div class="flex items-center justify-between">
        <p class="text-xs text-gray-500">A Pagar</p>
        <p class="text-xs text-gray-400">({{ payableCount }})</p>
      </div>
      <p class="text-sm font-semibold text-blue-600">{{ formatCurrency(payableTotal) }}</p>
    </div>
    <div class="p-2 bg-white rounded-lg">
      <div class="flex items-center justify-between">
        <p class="text-xs text-gray-500">Pago</p>
        <p class="text-xs text-gray-400">({{ paidCount }})</p>
      </div>
      <p class="text-sm font-semibold text-rose-600">{{ formatCurrency(paidTotal) }}</p>
    </div>
  </div>
</div>

<!-- Área de Filtros -->
<div class="bg-white rounded-xl shadow-lg p-6 mb-6">
  <!-- Linha de Filtros -->
  <div class="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
    
    <!-- Botões de Tipo de Conta -->
    <div class="flex gap-2">
      <button
  @click="tableAccountType = 'receivable'"
  :class="[
    'px-4 py-2 rounded-lg transition-colors duration-200',
    tableAccountType === 'receivable'
      ? 'bg-primary-dark text-white'
      : 'bg-gray-100 text-gray-600 hover:bg-primary-dark hover:text-white'
  ]"
>
  A Receber
</button>
<button
  @click="tableAccountType = 'payable'"
  :class="[
    'px-4 py-2 rounded-lg transition-colors duration-200',
    tableAccountType === 'payable'
      ? 'bg-primary-dark text-white'
      : 'bg-gray-100 text-gray-600 hover:bg-primary-dark hover:text-white'
  ]"
>
  A Pagar
</button>
    </div>

    <!-- Status -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
      <select
        v-model="filters.status"
        class="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
      >
        <option value="">Todos</option>
        <option v-for="status in availableStatuses" :key="status" :value="status">
          {{ getStatusPtBr(status) }}
        </option>
      </select>
    </div>

    <!-- Data Inicial -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">Data Inicial</label>
      <Calendar v-model="filters.startDate" :locale="ptBR" dateFormat="dd/mm/yy" class="w-full" />
    </div>

    <!-- Data Final -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">Data Final</label>
      <Calendar v-model="filters.endDate" :locale="ptBR" dateFormat="dd/mm/yy" class="w-full" />
    </div>

    <!-- Campo de Busca -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
      <input
        type="text"
        v-model="filters.search"
        placeholder="Buscar por descrição ou nome..."
        class="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
      />
    </div>
  </div>

  <!-- Botão de Limpar Filtros -->
  <div class="mt-4 flex justify-end">
    <button
      @click="clearFilters"
      class="text-gray-600 hover:text-gray-800 flex items-center gap-2"
    >
      <i class="pi pi-filter-slash"></i>
      Limpar Filtros
    </button>
  </div>
</div>


    <!-- Tabela de Contas -->
      <div class="bg-white rounded-2xl shadow-lg">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {{ tableAccountType === 'payable' ? 'Fornecedor' : 'Cliente' }}
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Valor
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Centro de Custo
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tipo de Custo
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vencimento
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Data Agendada
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="account in filteredAccounts" :key="account.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ getEntityName(account) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm" :class="{'text-green-600': tableAccountType === 'receivable', 'text-red-600': tableAccountType === 'payable'}">
                {{ formatCurrency(account.value) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ account.costCenterName || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ account.costTypeName || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ formatDate(account.dueDate) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div class="flex items-center gap-2">
                  <span>{{ account.scheduledDate ? formatDate(account.scheduledDate) : '-' }}</span>
                  <button 
                    @click="showDatePicker(account)"
                    class="text-gray-400 hover:text-gray-600"
                    title="Editar data agendada"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                  :style="getStatusClass(getStatusPtBr(determineAccountStatus(account)))"
                >
                  {{ getStatusPtBr(determineAccountStatus(account)) }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <div class="flex gap-2">
                  <span v-if="account.isRecurring" class="text-primary-dark" title="Conta Recorrente">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd" />
                    </svg>
                  </span>
                  <button
                    @click="editAccount(account)"
                    class="text-blue-600 hover:text-blue-900"
                    title="Editar"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    @click="confirmDeleteAccount(account)"
                    class="text-red-600 hover:text-red-900"
                    title="Excluir"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Modal de Nova Conta -->
      <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto" @click.self="showModal = false">
        <div class="flex min-h-screen items-center justify-center p-4">
          <!-- Overlay -->
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

          <!-- Modal -->
          <div class="inline-block transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all relative max-w-[500px] w-[1000px]">
            <!-- Modal Header -->
            <div class="bg-primary-dark px-4 py-3">
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-medium text-white">Nova Conta</h3>
                <button
                  @click="showModal = false"
                  class="rounded-full p-1 text-white hover:bg-primary-darker focus:outline-none"
                >
                  <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Modal Body -->
            <div class="bg-white px-4 py-4 space-y-6">
              <!-- Primeira linha: Tipo e Banco -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="flex flex-col">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    v-model="newAccount.type"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    required
                  >
                    <option value="" disabled>Selecione um tipo</option>
                    <option value="payable">A Pagar</option>
                    <option value="receivable">A Receber</option>
                  </select>
                </div>

                <div class="flex flex-col">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Banco de Pagamento</label>
                  <select
                    v-model="newAccount.bankId"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    required
                  >
                    <option value="" disabled>Selecione um banco</option>
                    <option
                      v-for="bank in bankStore.banks"
                      :key="bank.id"
                      :value="bank.id"
                    >
                      {{ bank.name }} - {{ bank.agency }}/{{ bank.account }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Segunda linha: Cliente/Fornecedor -->
              <div class="flex flex-col">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  {{ newAccount.type === 'payable' ? 'Fornecedor' : 'Cliente' }}
                  <span v-if="isLoading" class="text-gray-400 ml-1">(Carregando...)</span>
                </label>
                <select
                  v-model="newAccount.entityId"
                  required
                  :disabled="isLoading"
                  class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Selecione...</option>
                  <option 
                    v-for="entity in entityList" 
                    :key="entity.id" 
                    :value="entity.id"
                    :title="entity.document ? `${entity.name} - ${entity.document}` : entity.name"
                  >
                    {{ entity.name }}
                    <span v-if="entity.document" class="text-gray-500">
                      - {{ entity.document }}
                    </span>
                  </option>
                </select>
              </div>

              <!-- Terceira linha: Centro de Custo e Tipo de Custo -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="flex flex-col">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Centro de Custo</label>
                  <input
                    type="text"
                    :value="newAccount.costCenterName || ''"
                    readonly
                    class="block w-full rounded-md border-gray-100 bg-gray-50 text-gray-700 focus:ring-0 sm:text-sm"
                    placeholder="Selecione um centro de custo acima"
                  />
                </div>

                <div class="flex flex-col">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Tipo de Custo</label>
                  <input
                    type="text"
                    :value="newAccount.costTypeName || ''"
                    readonly
                    class="block w-full rounded-md border-gray-100 bg-gray-50 text-gray-700 focus:ring-0 sm:text-sm"
                    placeholder="Selecione um tipo de custo acima"
                  />
                </div>
              </div>

              <!-- Quarta linha: Valor e Vencimento -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="flex flex-col">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Valor</label>
                  <input
                    type="number"
                    v-model="newAccount.value"
                    required
                    min="0"
                    step="0.01"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    placeholder="0,00"
                  />
                </div>

                <div class="flex flex-col">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Vencimento</label>
                  <input
                    type="date"
                    v-model="newAccount.dueDate"
                    required
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
              </div>

              <!-- Quinta linha: Recorrente e Data Agendada -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                  <span class="text-sm font-medium text-gray-700">Recorrente?</span>
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="newAccount.isRecurring"
                      class="sr-only peer"
                    />
                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    <span class="ml-3 text-sm font-medium text-gray-700">{{ newAccount.isRecurring ? 'Sim' : 'Não' }}</span>
                  </label>
                </div>

                <div class="flex flex-col">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Data Agendada</label>
                  <input
                    type="date"
                    v-model="newAccount.scheduledDate"
                    required
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
              </div>

              <!-- Sexta linha: Observações -->
              <div class="flex flex-col">
                <label class="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                <textarea
                  v-model="newAccount.notes"
                  rows="3"
                  class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="Digite observações adicionais"
                ></textarea>
              </div>
            </div>

            <!-- Modal Footer -->
            <div class="bg-gray-50 px-4 py-3 flex flex-row-reverse gap-2">
              <button
                type="button"
                @click="saveAccount"
                class="inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-dark hover:bg-primary-darker focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Salvar
              </button>
              <button
                type="button"
                @click="showModal = false"
                class="inline-flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal de Confirmação de Exclusão -->
      <Dialog v-model:visible="showDeleteModal" header="Confirmar Exclusão" modal :closable="false">
        <span>Tem certeza de que deseja excluir esta conta?</span>
        <template #footer>
          <button @click="deleteAccount(selectedAccountForDeletion.id, organizationId)" class="bg-red-500 text-white px-4 py-2 rounded">Excluir</button>
          <button @click="showDeleteModal = false" class="bg-gray-300 text-gray-700 px-4 py-2 rounded">Cancelar</button>
        </template>
      </Dialog>
   
      <!-- Modal do DatePicker -->
      <Dialog v-model:visible="showDatePickerDialog" header="Agendar Data" modal :closable="false">
        <div class="p-4">
          <Calendar 
            v-model="selectedDate" 
            :showIcon="true" 
            dateFormat="dd/mm/yy"
            :showTime="false"
            :showButtonBar="true"
            :locale="ptBR"
          />
        </div>
        <template #footer>
          <button @click="saveScheduledDate" class="bg-primary-dark hover:bg-primary-dark/90 text-white px-4 py-2 rounded">Salvar</button>
          <button @click="showDatePickerDialog = false" class="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded ml-2">Cancelar</button>
        </template>
      </Dialog>
  </div>  <!-- Close main container -->
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useAccountStore } from '@/stores/account'
import { useCustomerStore } from '@/stores/customer'
import { useSupplierStore } from '@/stores/supplier'
import { usePaymentStatusStore } from '@/stores/paymentStatus'
import { useCurrentOrganizationStore } from '@/stores/currentOrganization'
import { storeToRefs } from 'pinia'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'
import { useCostCenterStore } from '@/stores/costCenter'
import { useCostTypeStore } from '@/stores/costType'
import { useBankStore } from '@/stores/bank'
import Calendar from 'primevue/calendar'
import Dialog from 'primevue/dialog'
import { useAuthStore } from '@/stores/auth'

// Estado para o datepicker
const selectedAccountForDate = ref(null)
const showDatePickerDialog = ref(false)
const selectedDate = ref(null)

// Função para mostrar o datepicker
const showDatePicker = (account) => {
  selectedAccountForDate.value = account
  selectedDate.value = account.scheduledDate ? new Date(account.scheduledDate) : new Date()
  showDatePickerDialog.value = true
}

// Função para salvar a data agendada
const saveScheduledDate = async () => {
  try {
    if (!selectedAccountForDate.value || !selectedDate.value) return

    // Formata a data para ISO string
    const formattedDate = selectedDate.value instanceof Date 
      ? selectedDate.value.toISOString().split('T')[0]
      : new Date(selectedDate.value).toISOString().split('T')[0]

    // Only include fields that exist in the original account
    const baseAccount = { ...selectedAccountForDate.value }
    
    // Update only the specific fields we want to change
    const updateData = {
      ...baseAccount,
      scheduledDate: formattedDate,
      status: 'AGENDADO',
      updatedAt: new Date().toISOString()
    }

    // Remove any undefined or null values
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined || updateData[key] === null) {
        delete updateData[key]
      }
    })

    console.log('Update Data:', updateData)
    await accountStore.updateAccount(updateData)

    // Atualizar a lista de contas
    await accountStore.fetchAccounts(organizationId.value)
    
    showDatePickerDialog.value = false
    selectedAccountForDate.value = null
    selectedDate.value = null

    toast.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Data agendada atualizada com sucesso!',
      life: 3000
    })
  } catch (error) {
    console.error('Erro ao atualizar data agendada:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Erro ao atualizar data agendada',
      life: 3000
    })
  }
}

// Estado para o modal de exclusão
const showDeleteModal = ref(false)
const selectedAccountForDeletion = ref(null)

async function confirmDeleteAccount(account) {
  console.log('Dados enviados para exclusão:', account)
  selectedAccountForDeletion.value = account
  showDeleteModal.value = true
}

async function deleteAccount(accountId, organizationId) {
  try {
    if (!accountId) {
      throw new Error('ID da conta não encontrado');
    }

    // Exclui do banco de dados
    await accountStore.deleteAccount(accountId, organizationId);
    
    // Limpa a conta da lista local
    accountStore.accounts = accountStore.accounts.filter(a => a.id !== accountId);
    
    // Fecha o modal de confirmação
    showDeleteModal.value = false;
    selectedAccountForDeletion.value = null;
    
    toast.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Conta excluída com sucesso',
      life: 3000
    });
  } catch (error) {
    console.error('Error deleting account:', error);
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Erro ao excluir conta: ' + error.message,
      life: 3000
    });
  }
}

// Utilities
const toast = useToast()

// Stores
const accountStore = useAccountStore()
const paymentStatusStore = usePaymentStatusStore()
const customerStore = useCustomerStore()
const supplierStore = useSupplierStore()
const currentOrganizationStore = useCurrentOrganizationStore()
const costCenterStore = useCostCenterStore()
const costTypeStore = useCostTypeStore()
const bankStore = useBankStore()
const authStore = useAuthStore()

// Estado reativo inicial
const showModal = ref(false)
const tableAccountType = ref('payable')
const isLoading = ref(false)
const loadingError = ref('')

// Obtém o primeiro e último dia do mês atual
const getCurrentMonthRange = () => {
  const now = new Date()
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  firstDay.setHours(0, 0, 0, 0)
  lastDay.setHours(23, 59, 59, 999)
  return { firstDay, lastDay }
}

const availableStatuses = computed(() => {
  return ['PAGO', 'PENDENTE', 'ATRASADO', 'AGENDADO', 'CANCELADO']
})

// Computed properties para os cards superiores
const monthTotals = computed(() => {
  // Filtrar contas do mês atual
  const monthAccounts = accountStore.accounts.filter(acc => {
    const dueDate = new Date(acc.dueDate)
    return dueDate >= new Date(filters.value.startDate) && dueDate <= new Date(filters.value.endDate)
  })

  // Separar por tipo
  const receivables = monthAccounts.filter(acc => acc.type === 'receivable')
  const payables = monthAccounts.filter(acc => acc.type === 'payable')

  // Função auxiliar para calcular totais por status
  const calculateTotalsByStatus = (accounts) => {
    const totals = {
      PAGO: accounts.filter(acc => determineAccountStatus(acc) === 'PAGO')
        .reduce((sum, acc) => sum + (acc.value || 0), 0),
      PENDENTE: accounts.filter(acc => determineAccountStatus(acc) === 'PENDENTE')
        .reduce((sum, acc) => sum + (acc.value || 0), 0),
      AGENDADO: accounts.filter(acc => determineAccountStatus(acc) === 'AGENDADO')
        .reduce((sum, acc) => sum + (acc.value || 0), 0),
      ATRASADO: accounts.filter(acc => determineAccountStatus(acc) === 'ATRASADO')
        .reduce((sum, acc) => sum + (acc.value || 0), 0)
    }

    const counts = {
      PAGO: accounts.filter(acc => determineAccountStatus(acc) === 'PAGO').length,
      PENDENTE: accounts.filter(acc => determineAccountStatus(acc) === 'PENDENTE').length,
      AGENDADO: accounts.filter(acc => determineAccountStatus(acc) === 'AGENDADO').length,
      ATRASADO: accounts.filter(acc => determineAccountStatus(acc) === 'ATRASADO').length
    }

    return { totals, counts }
  }

  // Calcular totais para recebíveis
  const receivableTotals = calculateTotalsByStatus(receivables)
  // Calcular totais para pagáveis
  const payableTotals = calculateTotalsByStatus(payables)

  return {
    receivable: {
      total: Object.values(receivableTotals.totals).reduce((a, b) => a + b, 0),
      received: receivableTotals.totals.PAGO,
      pending: receivableTotals.totals.PENDENTE,
      scheduled: receivableTotals.totals.AGENDADO,
      overdue: receivableTotals.totals.ATRASADO,
      receivedCount: receivableTotals.counts.PAGO,
      pendingCount: receivableTotals.counts.PENDENTE,
      scheduledCount: receivableTotals.counts.AGENDADO,
      overdueCount: receivableTotals.counts.ATRASADO
    },
    payable: {
      total: Object.values(payableTotals.totals).reduce((a, b) => a + b, 0),
      paid: payableTotals.totals.PAGO,
      pending: payableTotals.totals.PENDENTE,
      scheduled: payableTotals.totals.AGENDADO,
      overdue: payableTotals.totals.ATRASADO,
      paidCount: payableTotals.counts.PAGO,
      pendingCount: payableTotals.counts.PENDENTE,
      scheduledCount: payableTotals.counts.AGENDADO,
      overdueCount: payableTotals.counts.ATRASADO
    },
    balance: {
      total: Object.values(receivableTotals.totals).reduce((a, b) => a + b, 0) - 
             Object.values(payableTotals.totals).reduce((a, b) => a + b, 0),
      received: receivableTotals.totals.PAGO,
      paid: payableTotals.totals.PAGO,
      pending: receivableTotals.totals.PENDENTE - payableTotals.totals.PENDENTE,
      scheduled: receivableTotals.totals.AGENDADO - payableTotals.totals.AGENDADO,
      overdue: receivableTotals.totals.ATRASADO - payableTotals.totals.ATRASADO
    }
  }
})

// Computed properties para os valores dos mini cards
const receivableTotal = computed(() => {
  const total = monthTotals.value.receivable.pending + 
                monthTotals.value.receivable.scheduled + 
                monthTotals.value.receivable.overdue
  return total
})

const receivedTotal = computed(() => monthTotals.value.receivable.received)
const receivableCount = computed(() => monthTotals.value.receivable.pendingCount + monthTotals.value.receivable.scheduledCount + monthTotals.value.receivable.overdueCount)
const receivedCount = computed(() => monthTotals.value.receivable.receivedCount)

const payableTotal = computed(() => {
  const total = monthTotals.value.payable.pending + 
                monthTotals.value.payable.scheduled + 
                monthTotals.value.payable.overdue
  return total
})

const paidTotal = computed(() => monthTotals.value.payable.paid)
const payableCount = computed(() => monthTotals.value.payable.pendingCount + monthTotals.value.payable.scheduledCount + monthTotals.value.payable.overdueCount)
const paidCount = computed(() => monthTotals.value.payable.paidCount)

// Computed property para o saldo projetado
const filteredProjectedBalance = computed(() => {
  const totalReceivable = monthTotals.value.receivable.pending + 
                         monthTotals.value.receivable.scheduled + 
                         monthTotals.value.receivable.overdue + 
                         monthTotals.value.receivable.received

  const totalPayable = monthTotals.value.payable.pending + 
                      monthTotals.value.payable.scheduled + 
                      monthTotals.value.payable.overdue + 
                      monthTotals.value.payable.paid

  return totalReceivable - totalPayable
})

// Computed properties para valores pendentes
const filteredPendingTotal = computed(() => {
  return accountStore.accounts
    .filter(account => account.status === 'PENDENTE')
    .reduce((sum, account) => sum + (account.value || 0), 0)
})

const filteredPendingCount = computed(() => {
  return accountStore.accounts
    .filter(account => account.status === 'PENDENTE')
    .length
})

// Computed properties para valores agendados
const filteredScheduledTotal = computed(() => {
  return accountStore.accounts
    .filter(account => account.status === 'AGENDADO')
    .reduce((sum, account) => sum + (account.value || 0), 0)
})

const filteredScheduledCount = computed(() => {
  return accountStore.accounts
    .filter(account => account.status === 'AGENDADO')
    .length
})

// Computed property separada para a tabela (usando os filtros)
const filteredAccounts = computed(() => {
  const startDate = new Date(filters.value.startDate)
  const endDate = new Date(filters.value.endDate)
  startDate.setHours(0, 0, 0, 0)
  endDate.setHours(23, 59, 59, 999)

  // Filtra as contas localmente ao invés de buscar do servidor
  return accountStore.accounts.filter(account => {
    const dueDate = new Date(account.dueDate)
    dueDate.setHours(12, 0, 0, 0)
    
    // Filtros da tabela
    const matchesType = account.type === tableAccountType.value
    const matchesDate = dueDate >= startDate && dueDate <= endDate
    const matchesStatus = !filters.value.status || filters.value.status === '' || account.status === filters.value.status
    const matchesSearch = !filters.value.search || 
      account.description?.toLowerCase().includes(filters.value.search.toLowerCase()) ||
      getEntityName(account).toLowerCase().includes(filters.value.search.toLowerCase())

    return matchesType && matchesDate && matchesStatus && matchesSearch
  })
})

// Inicializa os filtros com o mês atual
const currentMonthRange = getCurrentMonthRange()
const filters = ref({
  startDate: formatDateToISO(currentMonthRange.firstDay),
  endDate: formatDateToISO(currentMonthRange.lastDay),
  status: '',
  search: ''
})

// Estado reativo para a organização atual
const organizationId = computed(() => {
  const id = currentOrganizationStore.currentOrganization?.id
  if (!id) {
    console.error('No organization selected')
    return null
  }
  console.log('Organization ID:', id)
  return id
})

onMounted(async () => {
  if (!organizationId.value) return;

  try {
    isLoading.value = true;

    // Primeiro carregamento
    await loadAccounts();

    // Remover duplicatas
    const deletedCount = await accountStore.removeDuplicateAccounts();
    console.log(`Removidas ${deletedCount} contas duplicadas`);

    // Recarregar contas após a remoção
    await loadAccounts();

    // Carregar centros de custo e tipos de custo
    await costCenterStore.fetchCostCenters(organizationId.value);
    await costTypeStore.fetchCostTypes(organizationId.value);
  } catch (error) {
    console.error('Erro ao carregar dados iniciais:', error);
    loadingError.value = 'Erro ao carregar dados iniciais';
  } finally {
    isLoading.value = false;
  }
});

const entityList = computed(() => {
  if (isLoading.value) {
    console.log('Loading...')
    return []
  }
  
  const list = newAccount.value.type === 'payable' 
    ? supplierStore.suppliers 
    : customerStore.customers
    
  console.log(`Entity List (${newAccount.value.type}):`, list)
  return list
})

const selectedEntityCostInfo = computed(() => {
  if (!newAccount.value.entityId || isLoading.value) {
    console.log('No entity selected or loading')
    return null
  }
  
  const entity = newAccount.value.type === 'payable' 
    ? supplierStore.suppliers.find(s => s.id === newAccount.value.entityId)
    : customerStore.customers.find(c => c.id === newAccount.value.entityId)
    
  console.log('Selected entity:', entity)
  console.log('Entity cost centers:', entity?.costCenters)
    
  if (!entity?.costCenters?.length) {
    console.log('No cost centers found')
    return null
  }
  
  // Retorna o primeiro centro de custo
  const costCenter = entity.costCenters[0]
  console.log('Selected cost center:', costCenter)
  
  // Atualiza os campos do formulário
  newAccount.value.costCenterId = costCenter.costCenterId
  newAccount.value.costTypeId = costCenter.costTypeId
  newAccount.value.costCenterName = costCenter.costCenterName
  newAccount.value.costTypeName = costCenter.costTypeName
  
  return costCenter
})

const newAccount = ref({
  type: 'payable',
  entityId: '',
  value: 0,
  dueDate: '',
  scheduledDate: '',
  notes: '',
  costCenterId: '',
  costTypeId: '',
  costCenterName: '',
  costTypeName: '',
  isRecurring: false,
  bankId: ''
})

// Estado para filtro de data do saldo projetado
const balanceStartDate = ref(new Date(new Date().getFullYear(), new Date().getMonth(), 1))
const balanceEndDate = ref(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0))

// Watch para as datas do saldo projetado
watch([balanceStartDate, balanceEndDate], () => {
  console.log('Datas do saldo projetado alteradas:', {
    startDate: balanceStartDate.value,
    endDate: balanceEndDate.value
  })
})

// Watch para o tipo de conta
watch(tableAccountType, () => {
  // Não precisa recarregar as contas, só atualiza o filtro
  console.log('Tipo de conta alterado:', tableAccountType.value)
})

// Watch para o entityId de volta
watch(() => newAccount.value.entityId, (newValue) => {
  if (newValue && selectedEntityCostInfo.value) {
    newAccount.value.costCenterId = selectedEntityCostInfo.value.costCenterId
    newAccount.value.costTypeId = selectedEntityCostInfo.value.costTypeId
    newAccount.value.costCenterName = selectedEntityCostInfo.value.costCenterName
    newAccount.value.costTypeName = selectedEntityCostInfo.value.costTypeName
  } else {
    newAccount.value.costCenterId = ''
    newAccount.value.costTypeId = ''
    newAccount.value.costCenterName = ''
    newAccount.value.costTypeName = ''
  }
})

// Watch para o organizationId
watch(() => organizationId.value, async (newOrgId) => {
  if (newOrgId) {
    try {
      isLoading.value = true
      // Carregar apenas os dados essenciais inicialmente
      await Promise.all([
        loadAccounts(),
        costCenterStore.fetchCostCenters(newOrgId),
        costTypeStore.fetchCostTypes(newOrgId),
        bankStore.fetchBanks(newOrgId)
      ])
    } catch (error) {
      console.error('Error fetching data:', error)
      loadingError.value = 'Erro ao carregar dados'
    } finally {
      isLoading.value = false
    }
  }
})

onMounted(async () => {
  if (organizationId.value) {
    try {
      isLoading.value = true
      // Carregar apenas os dados essenciais inicialmente
      await Promise.all([
        loadAccounts(),
        costCenterStore.fetchCostCenters(organizationId.value),
        costTypeStore.fetchCostTypes(organizationId.value),
        bankStore.fetchBanks(organizationId.value)
      ])
    } catch (error) {
      console.error('Error loading data:', error)
      loadingError.value = 'Erro ao carregar dados'
    } finally {
      isLoading.value = false
    }
  }
})

// Watch for account type changes to load entities only when opening modal
watch(() => showModal.value, async (isOpen) => {
  if (!isOpen || !organizationId.value) return

  try {
    isLoading.value = true
    // Carregar fornecedores ou clientes dependendo do tipo inicial da conta
    if (newAccount.value.type === 'payable' && supplierStore.suppliers.length === 0) {
      await supplierStore.fetchSuppliers(organizationId.value)
    } else if (newAccount.value.type === 'receivable' && customerStore.customers.length === 0) {
      await customerStore.fetchCustomers(organizationId.value)
    }
  } catch (error) {
    console.error('Error loading entity data:', error)
    toast.error('Erro ao carregar dados de clientes/fornecedores')
  } finally {
    isLoading.value = false
  }
})

// Watch for account type changes to load entities if needed
watch(() => newAccount.value.type, async (newType) => {
  if (!showModal.value) return
  
  try {
    isLoading.value = true
    // Carregar apenas se ainda não tiver os dados
    if (newType === 'payable' && supplierStore.suppliers.length === 0) {
      await supplierStore.fetchSuppliers(organizationId.value)
    } else if (newType === 'receivable' && customerStore.customers.length === 0) {
      await customerStore.fetchCustomers(organizationId.value)
    }
  } catch (error) {
    console.error('Error loading entity data:', error)
    toast.error('Erro ao carregar dados de clientes/fornecedores')
  } finally {
    isLoading.value = false
  }
})

// Função para carregar as contas
const loadAccounts = async () => {
  if (!organizationId.value) return

  try {
    isLoading.value = true
    await accountStore.fetchAccounts(organizationId.value)
    console.log('Contas carregadas:', accountStore.accounts)
  } catch (error) {
    console.error('Erro ao carregar contas:', error)
    loadingError.value = 'Erro ao carregar contas'
  } finally {
    isLoading.value = false
  }
}

// Função para limpar o formulário
const clearForm = () => {
  newAccount.value = {
    type: tableAccountType.value,
    entityId: '',
    value: 0,
    dueDate: '',
    scheduledDate: '',
    notes: '',
    costCenterId: '',
    costTypeId: '',
    costCenterName: '',
    costTypeName: '',
    isRecurring: false,
    bankId: ''
  }
}

// Função para abrir o modal de nova conta
const openNewAccountModal = async () => {
  clearForm()
  showModal.value = true

  try {
    await Promise.all([
      customerStore.fetchCustomers(),
      supplierStore.fetchSuppliers()
    ])
  } catch (error) {
    console.error('Error loading entity data:', error)
    toast.add({ 
      severity: 'error',
      summary: 'Erro',
      detail: 'Erro ao carregar dados das entidades',
      life: 3000
    })
  }
}

// Functions
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value)
}

function formatDate(date: any): string {
  if (!date) return ''
  
  try {
    // Se for um objeto Date
    if (date instanceof Date) {
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    }
    
    // Se for uma string com /
    if (typeof date === 'string' && date.includes('/')) {
      return date
    }
    
    // Se for uma string com -
    if (typeof date === 'string' && date.includes('-')) {
      const [year, month, day] = date.split('-')
      return `${day}/${month}/${year}`
    }
    
    // Tenta converter para Date
    const dateObj = new Date(date)
    if (!isNaN(dateObj.getTime())) {
      const day = String(dateObj.getDate()).padStart(2, '0')
      const month = String(dateObj.getMonth() + 1).padStart(2, '0')
      const year = dateObj.getFullYear()
      return `${day}/${month}/${year}`
    }
    
    return ''
  } catch (error) {
    console.error('Erro ao formatar data:', error)
    return ''
  }
}

function formatDateToDisplay(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}-${month}-${year}`
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'PAGO':
      return 'background-color: rgba(34, 197, 94, 0.2); color: #000000;'
    case 'PENDENTE':
      return 'background-color: rgba(234, 179, 8, 0.2); color: #000000;'
    case 'ATRASADO':
      return 'background-color: rgba(239, 68, 68, 0.2); color: #000000;'
    case 'AGENDADO':
      return 'background-color: rgba(59, 130, 246, 0.2); color: #000000;'
    default:
      return 'background-color: rgba(107, 114, 128, 0.2); color: #000000;'
  }
}

function getStatusPtBr(status: string): string {
  switch (status) {
    case 'paid':
      return 'PAGO'
    case 'pending':
      return 'PENDENTE'
    case 'overdue':
      return 'ATRASADO'
    case 'scheduled':
      return 'AGENDADO'
    case 'cancelled':
      return 'CANCELADO'
    default:
      return status.toUpperCase()
  }
}

function getStatusEn(status: string): string {
  switch (status) {
    case 'PAGO':
      return 'paid'
    case 'PENDENTE':
      return 'pending'
    case 'ATRASADO':
      return 'overdue'
    case 'AGENDADO':
      return 'scheduled'
    case 'CANCELADO':
      return 'cancelled'
    default:
      return status.toLowerCase()
  }
}

function determineAccountStatus(account) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const dueDate = new Date(account.dueDate)
  dueDate.setHours(0, 0, 0, 0)
  
  const scheduledDate = account.scheduledDate ? new Date(account.scheduledDate) : null
  if (scheduledDate) scheduledDate.setHours(0, 0, 0, 0)
  
  // Se a conta está cancelada
  if (account.status === 'CANCELADO') return 'CANCELADO'
  
  // Se tem data agendada
  if (scheduledDate) {
    // Se a data agendada é menor que hoje, a conta está paga
    if (scheduledDate < today) {
      return 'PAGO'
    }
    // Se a data agendada é hoje ou futura, conta está agendada
    return 'AGENDADO'
  }
  
  // Se não tem data agendada, verifica o vencimento
  if (dueDate < today) {
    return 'ATRASADO'
  }
  
  // Se a data de vencimento é hoje ou futura, está pendente
  return 'PENDENTE'
}

function getEntityName(account: any): string {
  if (!account || !account.entityId) return 'N/A'

  try {
    // Se temos o nome da entidade diretamente na conta, use-o
    if (account.entityName) {
      return account.entityName
    }

    // Se temos a entidade no store, use-a sem fazer chamada API
    if (account.type === 'payable') {
      const supplier = supplierStore.suppliers.find(s => s.id === account.entityId)
      return supplier?.name || 'N/A'
    } else if (account.type === 'receivable') {
      const customer = customerStore.customers.find(c => c.id === account.entityId)
      return customer?.name || 'N/A'
    }

    return 'N/A'
  } catch (error) {
    console.error('Erro ao obter nome da entidade:', error)
    return 'N/A'
  }
}

async function saveAccount() {
  if (!validateForm()) return

  if (!organizationId.value) {
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Nenhuma organização selecionada',
      life: 3000
    })
    return
  }

  try {
    // Formatar os dados da conta
    const accountData = {
      ...newAccount.value,
      status: newAccount.value.id ? newAccount.value.status : 'PENDENTE',
      organizationId: organizationId.value,
      value: Number(newAccount.value.value),
      type: newAccount.value.type as 'payable' | 'receivable',
      [newAccount.value.type === 'payable' ? 'supplierId' : 'customerId']: newAccount.value.entityId,
      costCenterId: newAccount.value.costCenterId,
      costTypeId: newAccount.value.costTypeId,
      costCenterName: newAccount.value.costCenterName,
      costTypeName: newAccount.value.costTypeName,
      dueDate: newAccount.value.dueDate,
      notes: newAccount.value.notes || ''
    }

    // Adicionar o nome da entidade
    if (accountData.type === 'payable') {
      const supplier = supplierStore.suppliers.find(s => s.id === accountData.supplierId)
      if (supplier) {
        accountData.entityName = supplier.name
      }
    } else {
      const customer = customerStore.customers.find(c => c.id === accountData.customerId)
      if (customer) {
        accountData.entityName = customer.name
      }
    }

    // Adicionar campos opcionais apenas se tiverem valor
    if (newAccount.value.scheduledDate) {
      accountData.scheduledDate = newAccount.value.scheduledDate
    }

    // Remover campos undefined ou null
    Object.keys(accountData).forEach(key => {
      if (accountData[key] === undefined || accountData[key] === null) {
        delete accountData[key]
      }
    })

    if (newAccount.value.id) {
  // Atualizar conta existente
  const accountId = newAccount.value.id
  const updateData = {
    ...accountData,
    updatedAt: new Date().toISOString()
  }
  
  await accountStore.updateAccount(accountId, updateData)
      
      toast.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Conta atualizada com sucesso',
        life: 3000
      })
    } else {
      // Criar nova conta
      await accountStore.createAccount(accountData)
      
      toast.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Conta criada com sucesso',
        life: 3000
      })
    }

    // Fechar modal e recarregar contas
    showModal.value = false
    await loadAccounts()
  } catch (error) {
    console.error('Erro ao salvar conta:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Erro ao salvar conta',
      life: 3000
    })
  }
}

function validateForm() {
  if (!newAccount.value.type) {
    alert('Por favor, selecione o tipo da conta')
    return false
  }
  if (!newAccount.value.entityId) {
    alert('Por favor, selecione um ' + (newAccount.value.type === 'payable' ? 'fornecedor' : 'cliente'))
    return false
  }
  if (!newAccount.value.value || newAccount.value.value <= 0) {
    alert('Por favor, informe um valor válido')
    return false
  }
  if (!newAccount.value.dueDate) {
    alert('Por favor, selecione a data de vencimento')
    return false
  }
  return true
}

async function applyFilters() {
  isLoading.value = true
  try {
    await accountStore.fetchAccounts(organizationId.value) // Removido o filtro de tipo
  } catch (error) {
    console.error('Erro ao aplicar filtros:', error)
    loadingError.value = 'Erro ao aplicar filtros. Por favor, tente novamente.'
  } finally {
    isLoading.value = false
  }
}

function editAccount(account) {
  newAccount.value = { ...account };
  showModal.value = true;
}

async function cancelAccount(account) {
  if (!account.id) return

  const accountStore = useAccountStore()
  try {
    accountStore.updateAccount(account.id, {
      ...account,
      status: 'CANCELADO'
    })
    toast.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Conta cancelada com sucesso',
      life: 3000
    })
  } catch (error) {
    console.error('Erro ao cancelar conta:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Erro ao cancelar conta',
      life: 3000
    })
  }
}

function formatDateToISO(date: Date): string {
  return date.toISOString().split('T')[0]
}

watch([() => filters.value.startDate, () => filters.value.endDate], () => {
  console.log('Datas alteradas:', {
    startDate: filters.value.startDate,
    endDate: filters.value.endDate
  })
})

// Configuração do locale para o Calendar
const ptBR = {
  firstDayOfWeek: 0,
  dayNames: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
  dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  dayNamesMin: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sa'],
  monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
  monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
  today: 'Hoje',
  clear: 'Limpar'
} as const;

// Computed properties para os subcards de Total a Receber
const totalPendingReceivable = computed(() => monthTotals.value.receivable.pending)
const totalPendingReceivableCount = computed(() => monthTotals.value.receivable.pendingCount)
const totalScheduledReceivable = computed(() => monthTotals.value.receivable.scheduled)
const totalScheduledReceivableCount = computed(() => monthTotals.value.receivable.scheduledCount)
const totalOverdueReceivable = computed(() => monthTotals.value.receivable.overdue)
const totalOverdueReceivableCount = computed(() => monthTotals.value.receivable.overdueCount)

// Computed properties para os subcards de Total a Pagar
const totalPendingPayable = computed(() => monthTotals.value.payable.pending)
const totalPendingPayableCount = computed(() => monthTotals.value.payable.pendingCount)
const totalScheduledPayable = computed(() => monthTotals.value.payable.scheduled)
const totalScheduledPayableCount = computed(() => monthTotals.value.payable.scheduledCount)
const totalOverduePayable = computed(() => monthTotals.value.payable.overdue)
const totalOverduePayableCount = computed(() => monthTotals.value.payable.overdueCount)

// Função auxiliar para verificar se uma conta está paga
const isPaid = (account: any) => account.status === 'PAGO'
</script>

<style scoped>
:deep(.p-accordion) {
  .p-accordion-header-link {
    @apply py-2 px-3 bg-white hover:bg-gray-50 border border-gray-100;
  }

  .p-accordion-content {
    @apply p-3 border border-t-0 border-gray-100 bg-white;
  }
}

:deep(.p-calendar) {
  .p-inputtext {
    @apply rounded-xl border-gray-300 shadow-sm focus:border-primary focus:ring-primary transition-colors duration-200;
    padding: 0.5rem 1rem;
  }
  
  .p-button {
    @apply bg-primary-light hover:bg-primary-light/80 focus:ring-primary-light transition-colors duration-200;
    border-top-right-radius: 0.75rem;
    border-bottom-right-radius: 0.75rem;
    border: none;
  }
}

:deep(.p-button) {
  background-color: var(--primary-color-light);
  border-color: var(--primary-color-light);
  
  &:hover {
    background-color: var(--primary-color-light-hover);
    border-color: var(--primary-color-light-hover);
  }
}

:deep(.p-multiselect) {
  @apply rounded-xl border-gray-300 shadow-sm focus:border-primary focus:ring-primary transition-colors duration-200;
  
  .p-multiselect-label {
    padding: 0.5rem 1rem;
  }
  
  &.p-multiselect-chip .p-multiselect-token {
    @apply bg-primary text-white;
    padding: 0.25rem 0.75rem;
    border-radius: 1rem;
  }
}

:deep(.p-calendar-w-btn) {
  @apply rounded-xl;
  
  .p-inputtext {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
}
</style>